import { useEffect, useMemo, useState } from "react";

import { API_ROUTES } from "@/app/constants/api.routes";
import { APP_LOCAL_KEYS } from "@/app/constants/constants";
import { REQUEST_METHODS } from "@/app/constants/global-constants";
import { customToast } from "@/components/alerts/CustomToast";
import { typedLocalStorage } from "@/utils/storage/localStorage";

type AppBootstrapServiceError = "MAINTENANCE" | "SERVER" | null;

type AppBootstrapState = {
  isLoading: boolean;
  isValidCompany: boolean;
  serviceError: AppBootstrapServiceError;
};

type UseAppBootstrap = AppBootstrapState & {
  subdomain: string;
  domainParts: string[];
};

const PUBLIC_SUBDOMAINS = new Set(["www", "workplanx"]);
const RESERVED_SUBDOMAINS = new Set(["admin", "customer"]);

type StoredWorkspaceData = {
  subDomain?: string;
};

type ServiceUnavailablePayload = {
  type?: string;
  data?: {
    type?: string;
  };
  error?: {
    type?: string;
  };
};

const isMaintenanceUnavailablePayload = (payload: unknown): boolean => {
  if (typeof payload !== "object" || payload === null) {
    return false;
  }

  const servicePayload = payload as ServiceUnavailablePayload;
  const type =
    servicePayload.type ??
    servicePayload.data?.type ??
    servicePayload.error?.type;

  return type?.toLowerCase() === "maintenance";
};

const getServiceUnavailableError = async (
  response: Response,
): Promise<AppBootstrapServiceError> => {
  if (response.status !== 503) {
    return null;
  }

  try {
    const payload = (await response.clone().json()) as unknown;

    return isMaintenanceUnavailablePayload(payload) ? "MAINTENANCE" : "SERVER";
  } catch {
    return "SERVER";
  }
};

const getStoredWorkspaceSubdomain = (value: unknown): string => {
  if (typeof value !== "object" || value === null) {
    return "";
  }

  const subDomain = (value as StoredWorkspaceData).subDomain;

  if (typeof subDomain !== "string") {
    return "";
  }

  return subDomain.toLowerCase();
};

/**
 * Custom React hook to bootstrap the application based on the current domain/subdomain.
 *
 * This hook:
 * - Extracts the subdomain and domain parts from the browser's hostname.
 * - Stores the subdomain in local storage.
 * - Determines if the current domain is public, reserved, or associated with a valid company workspace.
 * - Fetches workspace information from the API if necessary.
 * - Manages loading and validity state for the company workspace.
 *
 * @returns {UseAppBootstrap} - An object containing:
 *   - `isLoading`: Indicates if the bootstrap process is ongoing.
 *   - `isValidCompany`: Indicates if the current domain/subdomain is valid for a company workspace.
 *   - `subdomain`: The extracted subdomain from the hostname.
 *   - `domainParts`: The array of domain parts from the hostname.
 *
 * @example
 * const { isLoading, isValidCompany, subdomain, domainParts } = useAppBootstrap();
 */
const useAppBootstrap = (): UseAppBootstrap => {
  const domainParts = useMemo(() => window.location.hostname.split("."), []);
  const subdomain = useMemo(() => domainParts[0] ?? "", [domainParts]);

  const [state, setState] = useState<AppBootstrapState>({
    isLoading: true,
    isValidCompany: true,
    serviceError: null,
  });

  useEffect(() => {
    typedLocalStorage.setItem(APP_LOCAL_KEYS.SUB_DOMAIN, subdomain);
  }, [subdomain]);

  useEffect(() => {
    const resolveBootstrap = async (): Promise<void> => {
      const loweredSubdomain = subdomain.toLowerCase();
      const isPublicDomain =
        domainParts.length === 1 || PUBLIC_SUBDOMAINS.has(loweredSubdomain);
      const isReservedDomain = RESERVED_SUBDOMAINS.has(loweredSubdomain);

      if (isPublicDomain || isReservedDomain) {
        setState({
          isLoading: false,
          isValidCompany: true,
          serviceError: null,
        });

        return;
      }

      const storedWorkspaceId = typedLocalStorage.getItem(
        APP_LOCAL_KEYS.WORKSPACE_ID,
      );
      const storedWorkspaceData = typedLocalStorage.getItem(
        APP_LOCAL_KEYS.WORKSPACE_DATA,
      );
      const storedWorkspaceSubdomain =
        getStoredWorkspaceSubdomain(storedWorkspaceData);

      if (
        storedWorkspaceId &&
        storedWorkspaceSubdomain &&
        storedWorkspaceSubdomain === loweredSubdomain
      ) {
        setState({
          isLoading: false,
          isValidCompany: true,
          serviceError: null,
        });

        return;
      }

      if (storedWorkspaceId || storedWorkspaceData) {
        typedLocalStorage.removeItem(APP_LOCAL_KEYS.WORKSPACE_ID);
        typedLocalStorage.removeItem(APP_LOCAL_KEYS.WORKSPACE_DATA);
      }

      try {
        const response = await fetch(`${API_ROUTES.WORKSPACE}${subdomain}`, {
          method: REQUEST_METHODS.GET,
        });
        const serviceError = await getServiceUnavailableError(response);

        setState({
          isLoading: false,
          isValidCompany: response.ok,
          serviceError,
        });
      } catch (error) {
        customToast.error("Error fetching data");
        setState({
          isLoading: false,
          isValidCompany: false,
          serviceError: "SERVER",
        });
      }
    };

    void resolveBootstrap();
  }, [domainParts.length, subdomain]);

  return {
    ...state,
    subdomain,
    domainParts,
  };
};

export default useAppBootstrap;
